import {
  buildBlockSearchText,
  indexBlocksByParent,
  type SearchableBlock,
} from '@couplespace/core/search';

import type { LocalDatabase } from './types';

export const FTS_TABLE = 'block_search_fts';

export const FTS_MIGRATION = `
CREATE VIRTUAL TABLE IF NOT EXISTS ${FTS_TABLE} USING fts5(
  block_id UNINDEXED,
  title,
  body,
  tokenize = 'porter unicode61'
);
`;

const CONTAINER_TYPES = new Set(['page', 'database']);

let ftsSupportedCache: boolean | null = null;

export async function isFtsSupported(database: LocalDatabase): Promise<boolean> {
  if (ftsSupportedCache !== null) {
    return ftsSupportedCache;
  }
  const meta = await database.getFirstAsync<{ value: string }>(
    `SELECT value FROM sync_meta WHERE key = 'fts_supported'`,
  );
  if (meta) {
    ftsSupportedCache = meta.value === '1';
    return ftsSupportedCache;
  }

  try {
    await database.execAsync(
      `CREATE VIRTUAL TABLE IF NOT EXISTS _fts_probe USING fts5(content);`,
    );
    await database.execAsync(`DROP TABLE IF EXISTS _fts_probe;`);
    ftsSupportedCache = true;
  } catch {
    ftsSupportedCache = false;
  }

  await database.runAsync(
    `INSERT OR REPLACE INTO sync_meta (key, value) VALUES ('fts_supported', ?)`,
    ftsSupportedCache ? '1' : '0',
  );
  return ftsSupportedCache;
}

export async function ensureFtsTable(database: LocalDatabase): Promise<boolean> {
  const supported = await isFtsSupported(database);
  if (!supported) {
    return false;
  }
  const statements = FTS_MIGRATION.split(';')
    .map((s) => s.trim())
    .filter((s) => s.length > 0 && !s.startsWith('--'));
  for (const statement of statements) {
    await database.execAsync(`${statement};`);
  }
  return true;
}

type BlockRow = {
  id: string;
  parent_id: string | null;
  position: number;
  type: string;
  title: string | null;
  content: string;
  deleted_at: number | null;
};

function rowToSearchable(row: BlockRow): SearchableBlock {
  let content: Record<string, unknown> = {};
  try {
    content = JSON.parse(row.content) as Record<string, unknown>;
  } catch {
    content = {};
  }
  return {
    id: row.id,
    type: row.type,
    title: row.title,
    content,
    parentId: row.parent_id,
    position: row.position,
    deletedAt: row.deleted_at,
  };
}

async function loadActiveBlocks(database: LocalDatabase): Promise<SearchableBlock[]> {
  const rows = await database.getAllAsync<BlockRow>(
    `SELECT id, parent_id, position, type, title, content, deleted_at
     FROM blocks
     WHERE deleted_at IS NULL`,
  );
  return rows.map(rowToSearchable);
}

async function deleteFtsRow(database: LocalDatabase, blockId: string): Promise<void> {
  if (!(await isFtsSupported(database))) {
    return;
  }
  await database.runAsync(`DELETE FROM ${FTS_TABLE} WHERE block_id = ?`, blockId);
}

async function upsertFtsRow(
  database: LocalDatabase,
  blockId: string,
  title: string,
  body: string,
): Promise<void> {
  await deleteFtsRow(database, blockId);
  const trimmedTitle = title.trim();
  const trimmedBody = body.trim();
  if (!trimmedTitle && !trimmedBody) {
    return;
  }
  await database.runAsync(
    `INSERT INTO ${FTS_TABLE} (block_id, title, body) VALUES (?, ?, ?)`,
    blockId,
    trimmedTitle || ' ',
    trimmedBody || ' ',
  );
}

export async function clearSearchIndex(database: LocalDatabase): Promise<void> {
  if (!(await isFtsSupported(database))) {
    return;
  }
  await database.runAsync(`DELETE FROM ${FTS_TABLE}`);
}

export async function reindexBlockSearch(
  database: LocalDatabase,
  blockId: string,
  blocks?: SearchableBlock[],
): Promise<void> {
  const allBlocks = blocks ?? (await loadActiveBlocks(database));
  const target = allBlocks.find((block) => block.id === blockId);
  if (!target) {
    await deleteFtsRow(database, blockId);
    return;
  }
  const childrenByParentId = indexBlocksByParent(allBlocks);
  const { title, body } = buildBlockSearchText(target, childrenByParentId);
  await upsertFtsRow(database, blockId, title, body);
}

async function collectReindexTargets(
  database: LocalDatabase,
  blockId: string,
): Promise<string[]> {
  const targets = new Set<string>([blockId]);
  let currentId: string | null = blockId;

  while (currentId) {
    const row: { parent_id: string | null; type: string } | null =
      await database.getFirstAsync<{ parent_id: string | null; type: string }>(
        `SELECT parent_id, type FROM blocks WHERE id = ?`,
        currentId,
      );
    if (!row?.parent_id) {
      break;
    }
    if (CONTAINER_TYPES.has(row.type)) {
      targets.add(currentId);
    }
    targets.add(row.parent_id);
    currentId = row.parent_id;
  }

  return [...targets];
}

export async function reindexBlockSearchCascade(
  database: LocalDatabase,
  blockId: string,
): Promise<void> {
  if (!(await ensureFtsTable(database))) {
    return;
  }
  const allBlocks = await loadActiveBlocks(database);
  const activeIds = new Set(allBlocks.map((block) => block.id));
  const targets = (await collectReindexTargets(database, blockId)).filter((id) =>
    activeIds.has(id),
  );

  for (const id of new Set(targets)) {
    const block = allBlocks.find((row) => row.id === id);
    if (!block) {
      await deleteFtsRow(database, id);
      continue;
    }
    const childrenByParentId = indexBlocksByParent(allBlocks);
    const { title, body } = buildBlockSearchText(block, childrenByParentId);
    await upsertFtsRow(database, id, title, body);
  }

  if (!activeIds.has(blockId)) {
    await deleteFtsRow(database, blockId);
  }
}

export async function rebuildAllSearchIndex(database: LocalDatabase): Promise<void> {
  if (!(await ensureFtsTable(database))) {
    return;
  }
  await clearSearchIndex(database);
  const blocks = await loadActiveBlocks(database);
  const childrenByParentId = indexBlocksByParent(blocks);
  for (const block of blocks) {
    const { title, body } = buildBlockSearchText(block, childrenByParentId);
    await upsertFtsRow(database, block.id, title, body);
  }
}

export async function ensureSearchIndexMigrated(database: LocalDatabase): Promise<void> {
  if (!(await ensureFtsTable(database))) {
    return;
  }
  const row = await database.getFirstAsync<{ value: string }>(
    `SELECT value FROM sync_meta WHERE key = 'search_index_built'`,
  );
  if (row?.value === '1') {
    return;
  }
  await rebuildAllSearchIndex(database);
  await database.runAsync(
    `INSERT OR REPLACE INTO sync_meta (key, value) VALUES ('search_index_built', '1')`,
  );
}
